import {
  checkLocalStorage,
  defaultAdminAppPreferences,
  defaultEmptyUserPreferences,
  defaultUserPreferences,
  emailUsername,
  kvpsForSelectTheWorks,
  objectBy,
  objectById,
  setLocalStorage,
  sortByName,
  sortBySort
} from 'src/utilities/utils'
import Vue from 'vue'
import Constants from 'src/utilities/constants'
import axios from 'axios'
import notifications from './notifications'
import _ from 'lodash'
import {OK} from 'http-status-codes'
import {make} from 'vuex-pathify'
import {byId, crudifyMutations, resetState} from 'src/utilities/vuex2'
import {ptracGet, ptracGetEntities, ptracPatch, ptracPost, ptracPut} from 'src/utilities/ptracApi'
import {addHeaders} from 'src/utilities/authHeader'

const initialState = {
  results: [],
  inspectors: [],
  resultsMap: {},
  groups: [],
  user: {
    id: null,
    username: null,
    name: null,
    email: null,
    groups: [],
    preferences: defaultUserPreferences(),
    adminAppPreferences: defaultAdminAppPreferences(),
    daysOff: [],
    active: false
  },
  daysOffMap: {},
  allUserPreferences: defaultUserPreferences(),
  allUserPreferencesLoaded: false,
  autocompleteResults: []
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)
getters.byId = byId

getters.kvpsForSelect = (state) => {
  return kvpsForSelectTheWorks(state.results, {
    valueProp: 'id',
    labelProp: 'name',
    sortBy: 'name'
  })
}

getters.kvpsForSelectDeactivated = (state) => {
  const users = kvpsForSelectTheWorks(state.results, {
    valueProp: 'id',
    labelProp: 'name',
    sortBy: 'name'
  })

  return users.filter((user) => user.label.includes('Inactive'))
}

getters.kvpsForSelectActiveAndInactivate = (state) => {
  const users = kvpsForSelectTheWorks(state.results, {
    valueProp: 'id',
    labelProp: 'name',
    sortBy: 'name',
    includeInactive: true
  })

  return users
}

getters.findByNameStartsWith = (state) => (search) => {
  const results =  _.filter(state.results,
    (user) => user.name.toLocaleLowerCase().startsWith(search.toLowerCase()))
  const users = []
  results.forEach((user) => {
    users.push({
      name: user.name,
      id: user.id
    })
  })
  return users
}

getters.findByNameStartsWithExcludeInactive = (state) => (search) => {
  const results =  _.filter(state.results,
      (user) => user.name.toLocaleLowerCase().startsWith(search.toLowerCase()) && user.active)
  const users = []
  results.forEach((user) => {
    users.push({
      name: user.name,
      id: user.id
    })
  })
  return users
}

getters.displayName = (state) => (id) => {
  const user = objectById(state.results, id)
  if (user) {
    return user.name
  }
  return ''
}

getters.allUserPreferencesByType = (state) => (preferenceType) => {
  if (!state.allUserPreferences[preferenceType]) {
    state.allUserPreferences[preferenceType] = {}
  }
  return state.allUserPreferences[preferenceType]
}

getters.daysOffByMonthAndYear = (state) => (payload) => {
  const datesInMonth = state.user.daysOff.filter(dayOff => dayOff.getDate() === payload.day && dayOff.getMonth() === payload.month)

  const attributes = []

  datesInMonth.forEach(date => {
    attributes.push({
        key: date.getDate(),
        customData: {
          title: "AWAY",
          class: 'bg-pink-500 text-center text-black'
        },
        dates: date,
      }
    )
  })

  return attributes
}

getters.daysOffByMonthAndYearAllUsers = (state) => (currentPage) => {
  return _.get(state.daysOffMap, `${currentPage.year}-${currentPage.month}`)
}

const mutations = make.mutations(state)
mutations.reset = resetState
crudifyMutations(mutations)

mutations.setUserPreferencesEmptyValues = (state, payload) => {
  if (!state.user.preferences) {
    state.user.preferences = defaultUserPreferences()
  }
  state.user.preferences[payload.preferenceType] = payload.preferences
}

mutations.adminAppPreferences = (state, payload) => {
  state.user.adminAppPreferences[payload.preferenceType] = payload.sortColumns
}

mutations.resetUser = (state) => {
  state.user = JSON.parse(JSON.stringify(initialState.user))
}

mutations.loadUser = (state) => {
  state.user = JSON.parse(JSON.stringify(initialState.user))
}

mutations.hydrateUser = (state, user) => {
  Object.keys(state.user).forEach(key => {
    if (user.hasOwnProperty(key)) {
      state.user[key] = user[key]
    }
  })
}

mutations.setUserPreferences = (state, payload) => {
  if (!payload.preferences) {
    return
  }
  Object.keys(payload.preferences).forEach((key) => {
    const prefMeta = objectBy(state.allUserPreferences[payload.preferenceType], 'key', key)

    const userPreferenceValue = payload.preferences[key]
    state.user.preferences[payload.preferenceType][key] = userPreferenceValue

    // all values saved as strings in the DB, cast to explicit type
    if (prefMeta && prefMeta.hasOwnProperty('dataType')) {
      if (prefMeta.dataType === Constants.DATA_TYPES.BOOLEAN) {
        state.user.preferences[payload.preferenceType][key] = Boolean.parse(userPreferenceValue)
      } else if (prefMeta.dataType === Constants.DATA_TYPES.NUMBER) {
        state.user.preferences[payload.preferenceType][key] = Number(userPreferenceValue)
      }
    }
  })
}

mutations['user/addDayOff'] = (state, dateToAdd) => {
  if (!state.user.daysOff.includes(dateToAdd)) {
    state.user.daysOff.push(dateToAdd)
  }
  state.user.daysOff = state.user.daysOff.sort((a, b) => b - a);
}

mutations['user/removeDayOff'] = (state, dateToRemove) => {
  state.user.daysOff = state.user.daysOff.filter(dayOff => dayOff.getTime() !== dateToRemove.getTime())
}

mutations.updateUserResultDaysOff = (state, updatedUser) => {
  const foundUser = objectById(state.results, updatedUser.id)
  foundUser.daysOff = updatedUser.daysOff
}

mutations.daysOffAllUsers = (state, currentPage) => {
  if (!currentPage || currentPage.month === 0) {
    return
  }

  const currentPageKey = `${currentPage.year}-${currentPage.month}`

  state.daysOffMap[currentPageKey] = []

  state.results.forEach((user) => {
    if (user.daysOff) {
      user.daysOff.forEach((timestamp) => {
        const dayOff = new Date(timestamp)
        if (currentPage.year === dayOff.getFullYear() && currentPage.month === dayOff.getMonth() + 1) {
          const key = `${dayOff.getFullYear()}-${dayOff.getMonth() + 1}-${dayOff.getDate()}-${emailUsername(user.email)}`
          state.daysOffMap[currentPageKey].push({
            key: key,
            customData: {
              title: user.name,
            },
            dates: dayOff
          })
        }
      })
    }
  })
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions: {
    async updateUserGroups({ state }, payload) {
      return await ptracPatch(`users/${payload.id}`, {
        groups: payload.groups
      })
    },
    async updateUserIsActive({ state }, payload) {
      const response = await ptracPatch(`users/${payload.id}`, {
        active: payload.active
      })

      if (OK === response.status) {
        const updatedUser = state.results.find(user => user.id === payload.id)
        updatedUser.active = payload.active
        state.user.active = payload.active
      }

      return response
    },
    async fetchAll({ state }) {
      // if (checkLocalStorage(state, Constants.LOCAL_STORAGE.LOCATION.USER_DATA, Constants.LOCAL_STORAGE.EXPIRATION.ONE_DAY)
      //     && checkLocalStorage(state, Constants.LOCAL_STORAGE.LOCATION.USER_MAP_DATA, Constants.LOCAL_STORAGE.EXPIRATION.ONE_DAY)) {
      //   return
      // }

      state.results = []
      state.resultsMap = {}

      const ptracUsersResponse = await ptracGet('users/findAll')

      if (OK === ptracUsersResponse.status) {
        const ptracUsers = ptracUsersResponse.data

        for (const ptracUser of ptracUsers) {
          state.resultsMap[ptracUser.id] = ptracUser.name
        }

        state.results = sortByName(ptracUsers)
        setLocalStorage(Constants.LOCAL_STORAGE.LOCATION.USER_MAP_DATA, state.resultsMap)
        setLocalStorage(Constants.LOCAL_STORAGE.LOCATION.USER_DATA, state.results)
      }
    },
    async fetchAllInspectors({ state }) {
      const response = await ptracGet(`users/findByGroupsIn?groups=${Constants.GROUPS.CODE_INSPECTORS},${Constants.GROUPS.ENERGY_INSPECTORS}`)

      if (OK === response.status) {
        state.inspectors = response.data
      }

      return response
    },
    async fetchAllPreferences({state, commit}) {
      if (checkLocalStorage(state, Constants.LOCAL_STORAGE.LOCATION.ALL_USER_PREFERENCES, Constants.LOCAL_STORAGE.EXPIRATION.ONE_MONTH) || state.allUserPreferencesLoaded) {
        // checkLocalStorage needs to be be called first in case it has expired
        return state.allUserPreferences
      }

      const allPreferences = {}

      for (let preferenceTypeProp in Constants.USER_PREFERENCE_TYPES) {
        const preferenceType = Constants.USER_PREFERENCE_TYPES[preferenceTypeProp]
        const url = 'user-preferences/search/findByPreferenceTypeOrderBySortAsc?preferenceType=' + preferenceType
        const response = await ptracGetEntities(url)
        if (OK === response.status) {
          allPreferences[preferenceType] = sortBySort(response.data)
        }
      }

      commit('allUserPreferences', allPreferences)
      commit('allUserPreferencesLoaded', true)
      setLocalStorage(Constants.LOCAL_STORAGE.LOCATION.ALL_USER_PREFERENCES, state.allUserPreferences)

      return allPreferences
    },
    async fetchUserPreferences({ state, commit, dispatch }, payload) {
      const allPreferences = await dispatch('fetchAllPreferences')
      const preferencesWithEmptyValues = defaultEmptyUserPreferences(allPreferences[payload.preferenceType])

      const payload2 = {
        preferenceType: payload.preferenceType,
        preferences: preferencesWithEmptyValues
      }

      commit('setUserPreferencesEmptyValues', payload2)

      // throws a 404 if the user has never saved user preferences before
      let url = process.env.VUE_APP_PTRAC_API_URI + '/users/' + payload.userId
      const config = { headers: await addHeaders() }
      config.validateStatus = (status) => {
        return (status >= 200 && status < 300) || status === 404 // resolves promise instead of reject on 404
      }

      const response = await axios.get(url, config)

      if (response.status === 200) {
        if (!response.data.hasOwnProperty('preferences')) {
          response.data.preferences = {}
          response.data.preferences[payload.preferenceType] = {}
        }

        const payload3 = {
          preferenceType: payload.preferenceType,
          preferences: response.data.preferences[payload.preferenceType]
        }
        // user preference data was found, replace empty values with found values
        commit('setUserPreferences', payload3)
      }

      return response
    }
  },
  modules: {
    notifications
  }
}
